import { Dumbbell, GraduationCap, HeartPulse, Compass, Info, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const IMG_COURT =
  "https://images.unsplash.com/photo-1590227632180-80a3bf110871?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHx5b3V0aCUyMHNwb3J0cyUyMGJhc2tldGJhbGwlMjBtZW50b3JpbmclMjBkYXJrJTIwZHJhbWF0aWN8ZW58MHx8fHwxNzg3NDE3MDA4fDA&ixlib=rb-4.1.0&q=85";
const IMG_HUDDLE =
  "https://images.unsplash.com/photo-1520399636535-24741e71b160?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhdGhsZXRpYyUyMHRyYWluaW5nJTIwY29hY2glMjB0ZWFtJTIwaGlnaCUyMGNvbnRyYXN0fGVufDB8fHx8MTc4NzQxNzAwOHww&ixlib=rb-4.1.0&q=85";

const cardCls =
  "border border-[#242436] hover:border-[#8B5CF6]/50 transition-colors duration-300 bg-[#161622] hover:bg-[#1D1D2C] p-8 shadow-[0_4px_30px_rgba(0,0,0,0.5)] group";

const FeatureCard = ({ img, tag, title, body, tid, delay }) => (
  <Reveal delay={delay} className={`${cardCls} relative overflow-hidden min-h-[380px] flex flex-col justify-end`} >
    <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-[#08080C]/60 to-transparent" />
    <div className="relative">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB800]">{tag}</span>
      <h3 className="font-display font-black uppercase tracking-wide text-2xl sm:text-3xl text-[#F4F4F6] mt-2" data-testid={tid}>{title}</h3>
      <p className="text-sm leading-relaxed text-[#A1A1B3] mt-2 max-w-md">{body}</p>
    </div>
  </Reveal>
);

const SmallCard = ({ Icon, title, body, tid, delay }) => (
  <Reveal delay={delay} className={cardCls}>
    <div className="flex items-start justify-between">
      <Icon className="w-8 h-8 text-[#8B5CF6]" strokeWidth={2} />
      <ArrowUpRight className="w-5 h-5 text-[#62627A] group-hover:text-[#8B5CF6] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
    </div>
    <h3 className="font-display font-bold uppercase tracking-wide text-xl sm:text-2xl text-[#F4F4F6] mt-6" data-testid={tid}>{title}</h3>
    <p className="text-sm leading-relaxed text-[#A1A1B3] mt-3">{body}</p>
  </Reveal>
);

export const Programs = () => (
  <section id="nonprofit" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16" data-testid="programs-section">
    <Reveal>
      <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FFB800] mb-4">The Nonprofit · G.A.M.E. Mentorship and Training Inc.</div>
      <h2 className="font-display font-black uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-6xl text-[#F4F4F6] max-w-4xl">
        Train the body. <span className="text-outline">Coach the mind.</span>
      </h2>
    </Reveal>

    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
      <FeatureCard
        img={IMG_HUDDLE}
        tag="One-on-One & Group"
        title="Youth Mentorship"
        body="Accountability, confidence, discipline, communication, and goal setting — mentors who walk alongside every young person."
        tid="program-mentorship"
        delay={0}
      />
      <SmallCard
        Icon={GraduationCap}
        title="Academic Enrichment"
        body="Tutoring, academic goal setting, college preparation, and resources that make strong performance the expectation."
        tid="program-academics"
        delay={0.08}
      />
      <SmallCard
        Icon={Dumbbell}
        title="Athletic Development"
        body="Structured training to improve fitness, athletic performance, teamwork, mobility, conditioning, and healthy habits."
        tid="program-athletics"
        delay={0.16}
      />
      <SmallCard
        Icon={HeartPulse}
        title="Health & Wellness"
        body="Age-appropriate education and activities supporting nutrition, physical activity, mental resilience, and lifelong wellness."
        tid="program-wellness"
        delay={0}
      />
      <FeatureCard
        img={IMG_COURT}
        tag="Decision-Making & Teamwork"
        title="Leadership Development"
        body="Real opportunities to strengthen decision-making, communication, responsibility, and confidence — on and off the court."
        tid="program-leadership"
        delay={0.08}
      />
      <SmallCard
        Icon={Compass}
        title="College & Career Exposure"
        body="College visits, career exploration, workshops, and community partnerships that open real educational and professional pathways."
        tid="program-exposure"
        delay={0.16}
      />
    </div>

    <Reveal delay={0.1} className="mt-6">
      <div className={`${cardCls} flex flex-col sm:flex-row sm:items-center gap-6 justify-between`}>
        <div className="flex items-center gap-6">
          <Info className="w-10 h-10 text-[#FFB800] shrink-0" strokeWidth={1.75} />
          <div>
            <h3 className="font-display font-bold uppercase tracking-wide text-xl sm:text-2xl text-[#F4F4F6]" data-testid="programs-photo-note">A note on photography</h3>
            <p className="text-sm leading-relaxed text-[#A1A1B3] mt-1">Images on this site are illustrative stock photos and do not depict G.A.M.E. participants. Authentic program photos will be added as they are approved.</p>
          </div>
        </div>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#62627A] shrink-0">Whole-person development</span>
      </div>
    </Reveal>
  </section>
);
